import { cn } from "@/lib/utils";
import { AlertTriangle, Loader2, RotateCcw } from "lucide-react";
import { Component, ReactNode } from "react";
import {
  isChunkLoadError,
  isVersionSkewError,
  reloadForNewVersion,
} from "@/lib/lazy-retry";

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  isChunkError: boolean;
}

class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null, isChunkError: false };
  }

  static getDerivedStateFromError(error: Error): State {
    return {
      hasError: true,
      error,
      isChunkError: isChunkLoadError(error),
    };
  }

  componentDidCatch(error: Error) {
    // Stale-SPA recovery: chunk-load failures get an immediate reload. But
    // version-skew bugs after a deploy can also surface as runtime errors
    // like "Cannot read properties of null (reading 'useState')" when an
    // old cached index.html loads against new chunks. Those don't match
    // the chunk-load regex, so we attempt ONE cache-busting reload for any
    // catastrophic boot-time error too. reloadForNewVersion() is gated by
    // sessionStorage and capped at MAX_RELOADS, so a real bug still
    // surfaces this UI after the cap is hit.
    if (isChunkLoadError(error)) {
      reloadForNewVersion();
      return;
    }

    // Stale-bundle / version-skew runtime errors. The shared
    // isVersionSkewError() helper recognizes the same underlying
    // TypeError across V8 (Chrome/Edge/Android), JSCore (iOS Safari),
    // and Firefox — see client/src/lib/lazy-retry.ts. Real product bugs
    // get past the sessionStorage cap (MAX_RELOADS=3) and surface normally.
    if (isVersionSkewError(error)) {
      reloadForNewVersion();
    }
  }

  render() {
    if (this.state.hasError) {
      // Chunk-load failure (stale SPA after a redeploy). Show a specific,
      // friendly message, we're already reloading in componentDidCatch.
      if (this.state.isChunkError) {
        return (
          <div className="flex items-center justify-center min-h-screen p-8 bg-background">
            <div className="flex flex-col items-center w-full max-w-md p-8 text-center">
              <Loader2
                size={44}
                className="text-primary mb-6 flex-shrink-0 animate-spin"
                aria-hidden="true"
              />
              <h2 className="text-xl font-semibold tracking-tight mb-3">
                A newer version of Voom is available
              </h2>
              <p className="text-sm text-muted-foreground mb-6 tracking-wide">
                Updating your browser to the latest build. This takes a second…
              </p>
              <button
                onClick={() => reloadForNewVersion()}
                className={cn(
                  "flex items-center gap-2 px-5 py-2.5 rounded-full min-h-[44px]",
                  "bg-primary text-primary-foreground text-sm font-medium",
                  "hover:opacity-90 active:scale-[0.98] transition-all cursor-pointer"
                )}
              >
                <RotateCcw size={16} aria-hidden="true" />
                Reload now
              </button>
            </div>
          </div>
        );
      }

      // Generic error path, unchanged UX for real bugs
      return (
        <div className="flex items-center justify-center min-h-screen p-8 bg-background">
          <div className="flex flex-col items-center w-full max-w-2xl p-8">
            <AlertTriangle
              size={48}
              className="text-destructive mb-6 flex-shrink-0"
              aria-hidden="true"
            />

            <h2 className="text-xl mb-4">An unexpected error occurred.</h2>

            <p className="text-sm text-muted-foreground mb-6 text-center max-w-md">
              {this.state.error?.message ||
                "Something went wrong. Please try reloading the page."}
            </p>

            <button
              onClick={() => window.location.reload()}
              className={cn(
                "flex items-center gap-2 px-4 py-2 rounded-lg min-h-[44px]",
                "bg-primary text-primary-foreground",
                "hover:opacity-90 cursor-pointer"
              )}
            >
              <RotateCcw size={16} aria-hidden="true" />
              Reload Page
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
